/* eslint-disable react/no-danger */
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  useDownloadRegisterResultDocxFile,
  useDownloadRegisterResultPdfFile,
  useGetRegisterResult,
  useGetRegisterResultReviewHistories,
} from 'utils/hooks/registerResult';
import { autoDownloadFile } from 'utils/func';
import { Button } from 'primereact/button';
import GeneralInfo from '../GeneralInfo';
import RegisterContent from '../RegisterContent';
import './styles.scss';
import ExpenditureAndAppendix from '../ExpenditureAndAppendix';
import RegisterResultReviewHistory from '../../RegisterResultReviewHistory';

export default function ReviewForm({
  data,
  handleDownloadFile,
  registerResultIdUsed,
  isLiquidation,
}) {
  const { t } = useTranslation();
  const { projectId } = useParams();

  const [visible, setVisible] = useState(false);

  const { control, setValue } = useForm({ mode: 'onChange' });
  const { errors } = useFormState({ control });

  const { data: registerResult } = useGetRegisterResult(projectId, {
    enabled: !!registerResultIdUsed,
  });

  const { data: registerResultReviewHistories } = useGetRegisterResultReviewHistories(
    registerResultIdUsed,
    {}
  );

  const registerResultReviewHistoriesUsed = useMemo(() => {
    const result = [];
    let temp = [];
    registerResultReviewHistories?.forEach((item) => {
      if (item.action === 'reset') {
        result.push(temp);
        temp = [];
        temp.push(item);
      } else {
        temp.push(item);
      }
    });
    result.push(temp);
    return result;
  }, [registerResultReviewHistories]);

  useEffect(() => {
    if (registerResult) {
      // ParticipantTable
      if (registerResult?.registerResultAuthors) {
        setValue('registerResultAuthors', registerResult?.registerResultAuthors ?? []);
      }

      // SecurtityInformation
      setValue('securitySeverity', registerResult?.securitySeverity ?? '');

      // Product
      setValue('summaryReportQuantity.value', registerResult?.statistics?.summaryReportQuantity);
      setValue(
        'branchProposalReportQuantity.value',
        registerResult?.statistics?.branchProposalReportQuantity
      );
      setValue('thematicReportQuantity.value', registerResult?.statistics?.thematicReportQuantity);
      setValue('mapQuantity.value', registerResult?.statistics?.mapQuantity);
      setValue('filmQuantity.value', registerResult?.statistics?.filmQuantity);
      setValue('videoQuantity.value', registerResult?.statistics?.videoQuantity);
      setValue('imageQuantity.value', registerResult?.statistics?.imageQuantity);
      setValue('cdDiskQuantity.value', registerResult?.statistics?.cdDiskQuantity);
      setValue('documentQuantity.value', registerResult?.statistics?.documentQuantity);

      // Product Description
      setValue('registerResultStats', registerResult?.products ?? []);

      setValue('note', registerResult?.note ?? '');
      setValue('settlementAmount', registerResult?.settlementAmount ?? 0);
      setValue('savingsAmount', registerResult?.savingsAmount ?? 0);
      setValue('recoveryAmount', registerResult?.recoveryAmount ?? 0);
    }
  }, [registerResult]);

  const {
    mutateAsync: downloadDocxFile,
    isLoading: isDownloadDocxFileLoading,
    isFetching: isDownloadDocxFileFetching,
  } = useDownloadRegisterResultDocxFile();
  const {
    mutateAsync: downloadPdfFile,
    isLoading: isDownloadPdfFileLoading,
    isFetching: isDownloadPdfFileFetching,
  } = useDownloadRegisterResultPdfFile();

  const handleDownloadDocxFile = async () => {
    const res = await downloadDocxFile({ projectId });
    autoDownloadFile(res);
  };

  const handleDownloadPdfFile = async () => {
    const res = await downloadPdfFile({ projectId });
    autoDownloadFile(res);
  };

  return (
    <div className="review-form">
      <div className="flex justify-content-end mb-4">
        <Button
          type="button"
          label={t('registerResult.reviewHistory.title')}
          icon="pi pi-history"
          className="block w-auto h-3rem mr-4"
          onClick={() => setVisible(true)}
        />
        <Button
          type="button"
          label={t('registerResult.download.exportDocx')}
          icon={`pi ${
            isDownloadDocxFileLoading || isDownloadDocxFileFetching
              ? 'pi-spin pi-spinner'
              : 'pi-file-word'
          }`}
          className="block w-auto h-3rem mr-4"
          severity="info"
          onClick={handleDownloadDocxFile}
          disabled={
            isDownloadDocxFileLoading || isDownloadDocxFileFetching || !registerResultIdUsed
          }
        />
        <Button
          type="button"
          label={t('registerResult.download.exportPdf')}
          icon={`pi ${
            isDownloadPdfFileLoading || isDownloadPdfFileFetching
              ? 'pi-spin pi-spinner'
              : 'pi-file-pdf'
          }`}
          className="block w-auto h-3rem border-none"
          severity="danger"
          onClick={handleDownloadPdfFile}
          disabled={isDownloadPdfFileLoading || isDownloadPdfFileFetching || !registerResultIdUsed}
        />
      </div>
      <div className="mb-5">
        <div className="text-center">
          <p className="font-bold text-2xl my-0">{t('r09.title1')}</p>
          <p className=" text-2xl my-0">{t('r09.title2')}</p>
        </div>
      </div>
      <form autoComplete="off" className="p-fluid form-midterm-container">
        <GeneralInfo data={data} isLiquidation={isLiquidation} />
        <div className="mt-4">
          <RegisterContent
            disabled
            control={control}
            errors={errors}
            viewOnly
            registerResult={registerResult}
            participants={data?.projectProposal?.projectProposalContacts}
            handleDownloadFile={handleDownloadFile}
            isLiquidation={isLiquidation}
          />
        </div>
        <div className="mt-4">
          <ExpenditureAndAppendix
            disabled
            control={control}
            errors={errors}
            appendixeArray={registerResult?.appendixes || []}
            minuteFile={registerResult?.minuteFile}
            handleDownloadFile={handleDownloadFile}
            isLiquidation={isLiquidation}
          />
        </div>
        <div className="flex justify-content-end">
          <Button
            type="button"
            label={t('registerResult.reviewHistory.title')}
            icon="pi pi-history"
            className="block w-auto h-3rem mr-4"
            onClick={() => setVisible(true)}
          />
        </div>
      </form>

      <RegisterResultReviewHistory
        data={registerResultReviewHistoriesUsed || []}
        visible={visible}
        setVisible={setVisible}
        title={t('registerResult.reviewHistory.title')}
      />
    </div>
  );
}

ReviewForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object,
  handleDownloadFile: PropTypes.func,
  registerResultIdUsed: PropTypes.string,
  isLiquidation: PropTypes.bool,
};

ReviewForm.defaultProps = {
  data: {},
  handleDownloadFile: () => {},
  registerResultIdUsed: null,
  isLiquidation: false,
};
